import { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import api, { handleApiError, queryParams } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/formatters';
import { Package, Search, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, Eye, X, Upload, Check, LayoutGrid, List, ImageOff } from 'lucide-react';
import toast from 'react-hot-toast';

function ProductImage({ src, alt, className = '' }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div className={`flex items-center justify-center bg-surface-100 dark:bg-surface-800 ${className}`}>
        <ImageOff size={28} className="text-surface-300 dark:text-surface-600" aria-hidden="true" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className={className}
    />
  );
}

function ProductCard({ product, onView, onEdit, onDeactivate, onDelete, canEdit }) {
  const reduceMotion = useReducedMotion();
  const hasStock = product.stock !== undefined && product.stock !== null;
  const stock = Number(product.stock) || 0;
  const minStock = Number(product.min_stock) || 0;
  const lowStock = hasStock && minStock > 0 && stock <= minStock;

  return (
    <motion.article
      layout
      initial={reduceMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.2, ease: 'easeOut' }}
      className="group card flex h-full flex-col overflow-hidden"
    >
      <button
        type="button"
        onClick={() => onView(product)}
        className="relative block w-full overflow-hidden rounded-t-2xl text-left"
        aria-label={`Lihat detail ${product.name}`}
      >
        <div className="relative aspect-square w-full overflow-hidden bg-surface-100 dark:bg-surface-800">
          <ProductImage
            src={product.image_url}
            alt={product.name}
            className="h-full w-full object-cover transition-transform duration-500 ease-soft group-hover:scale-105"
          />
          <span className={`absolute left-2.5 top-2.5 rounded-full px-2.5 py-1 text-[11px] font-semibold backdrop-blur ${
            product.is_active
              ? 'bg-white/85 text-success-700 dark:bg-surface-900/80 dark:text-success-400'
              : 'bg-white/85 text-danger-600 dark:bg-surface-900/80 dark:text-danger-400'
          }`}>
            {product.is_active ? 'Aktif' : 'Nonaktif'}
          </span>
          {lowStock && (
            <span className="absolute right-2.5 top-2.5 rounded-full bg-warning-100/95 px-2.5 py-1 text-[11px] font-semibold text-warning-800">
              Stok rendah
            </span>
          )}
        </div>
      </button>

      <div className="flex flex-1 flex-col gap-2 p-3.5">
        <p className="text-[11px] font-medium uppercase tracking-wide text-surface-400 dark:text-surface-500">
          {product.category?.name || product.category_name || 'Tanpa kategori'}
        </p>
        <button type="button" onClick={() => onView(product)} className="text-left">
          <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-surface-900 hover:underline dark:text-white">
            {product.name}
          </h3>
        </button>
        <p className="truncate text-xs text-surface-400 dark:text-surface-500">SKU: {product.sku || '-'}</p>

        <div className="mt-auto flex items-end justify-between gap-2 pt-1">
          <div className="min-w-0">
            <p className="text-base font-semibold text-surface-900 dark:text-white">
              {formatCurrency(product.selling_price)}
            </p>
            <p className="text-[11px] text-surface-400 dark:text-surface-500">
              {hasStock ? (
                <>Stok: <span className={lowStock ? 'font-semibold text-warning-600 dark:text-warning-500' : 'font-medium'}>{stock}</span></>
              ) : (
                <>{product.unit?.name || product.unit_name || 'Tanpa satuan'}</>
              )}
              {hasStock && (product.unit?.name || product.unit_name) ? ` ${product.unit?.name || product.unit_name}` : ''}
            </p>
          </div>
          {canEdit && (
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => onEdit(product)} aria-label={`Edit ${product.name}`} className="btn-icon-sm rounded-lg text-surface-500 hover:text-warning-600">
                <Edit2 size={15} />
              </button>
              <button
                type="button"
                onClick={() => onDeactivate(product)}
                aria-label={`${product.is_active ? 'Nonaktifkan' : 'Aktifkan'} ${product.name}`}
                className={`btn-icon-sm rounded-lg ${product.is_active ? 'text-surface-500 hover:text-danger-600' : 'text-success-600 hover:text-success-700'}`}
              >
                {product.is_active ? <Trash2 size={15} /> : <Check size={15} />}
              </button>
              <button type="button" onClick={() => onDelete(product)} aria-label={`Hapus ${product.name}`} className="btn-icon-sm rounded-lg text-surface-500 hover:text-danger-600">
                <Trash2 size={15} />
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.article>
  );
}

function ProductRow({ product, onView, onEdit, onDeactivate, onDelete }) {
  return (
    <tr>
      <td>
        <div className="flex items-center gap-3">
          <ProductImage
            src={product.image_url}
            alt={product.name}
            className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
          />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{product.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">SKU: {product.sku} | Barcode: {product.barcode}</p>
          </div>
        </div>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200 truncate max-w-32" title={product.category?.name || product.category_name}>{product.category?.name || product.category_name || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{product.unit?.name || product.unit_name || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(product.selling_price)}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{product.purchase_price ? formatCurrency(product.purchase_price) : '-'}</span>
      </td>
      <td>
        <span className="text-sm font-medium text-gray-900 dark:text-white">{product.stock}</span>
      </td>
      <td>
        <span className={`badge ${product.is_active ? 'badge-success' : 'badge-danger'}`}>
          {product.is_active ? 'Aktif' : 'Nonaktif'}
        </span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <button onClick={() => onView(product)} className="p-1.5 text-primary-900 hover:bg-primary-50 dark:hover:bg-primary-950/50 rounded">
            <Eye className="h-4 w-4" />
          </button>
          <button onClick={() => onEdit(product)} className="p-1.5 text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded">
            <Edit2 className="h-4 w-4" />
          </button>
          <button onClick={() => onDeactivate(product)} className={`p-1.5 ${product.is_active ? 'text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20' : 'text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20'} rounded`}>
            {product.is_active ? <Trash2 className="h-4 w-4" /> : <Check className="h-4 w-4" />}
          </button>
          <button onClick={() => onDelete(product)} className="p-1.5 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function ProductsPage() {
  const { hasPermission } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', category_id: '', status: '', unit_id: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [form, setForm] = useState({ sku: '', barcode: '', name: '', category_id: '', unit_id: '', purchase_price: '', selling_price: '', min_stock: '', tax_id: '', valuation_method: 'fifo', is_active: true, description: '' });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'grid';
    return window.localStorage.getItem('stokku.products.view') || 'grid';
  });

  useEffect(() => {
    window.localStorage.setItem('stokku.products.view', viewMode);
  }, [viewMode]);

  useEffect(() => {
    fetchProducts();
    fetchLookupData();
  }, [pagination.page, filters]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/products', { params });
      if (res.data.success) {
        const payload = res.data.data;
        setProducts(Array.isArray(payload?.products) ? payload.products : (Array.isArray(payload) ? payload : []));
        const meta = res.data.pagination || payload || {};
        const total = Number(meta.total) || 0;
        setPagination((prev) => ({
          ...prev,
          total,
          totalPages: Number(meta.totalPages) || Math.ceil(total / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLookupData = async () => {
    try {
      const [catRes, unitRes, taxRes] = await Promise.all([
        api.get('/categories', { params: { limit: 1000 } }),
        api.get('/units', { params: { limit: 1000 } }),
        api.get('/taxes', { params: { limit: 1000 } }),
      ]);
      setCategories(catRes.data.data?.categories || catRes.data.data || []);
      setUnits(unitRes.data.data?.units || unitRes.data.data || []);
      setTaxes(taxRes.data.data?.taxes || taxRes.data.data || []);
    } catch {
      // Silently fail
    }
  };

  const uploadProductImage = async (productId) => {
    if (!imageFile) return;
    const formData = new FormData();
    formData.append('image', imageFile);
    await api.post(`/products/${productId}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/products', form);
      if (res.data.success) {
        const newId = res.data.data?.id;
        if (newId) {
          try {
            await uploadProductImage(newId);
          } catch (error) {
            handleApiError(error);
          }
        }
        toast.success('Produk berhasil dibuat');
        setShowCreateModal(false);
        resetForm();
        fetchProducts();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put(`/products/${selectedProduct.id}`, form);
      if (res.data.success) {
        try {
          await uploadProductImage(selectedProduct.id);
        } catch (error) {
          handleApiError(error);
        }
        toast.success('Produk berhasil diperbarui');
        setShowEditModal(false);
        setSelectedProduct(null);
        setImageFile(null);
        setImagePreview('');
        fetchProducts();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (product) => {
    if (!confirm(`Yakin ${product.is_active ? 'menonaktifkan' : 'mengaktifkan'} produk ${product.name}?`)) return;
    try {
      const res = await api.post(`/products/${product.id}/toggle-status`);
      if (res.data.success) {
        toast.success(`Produk ${product.is_active ? 'dinonaktifkan' : 'diaktifkan'}`);
        fetchProducts();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDelete = async (product) => {
    if (!confirm(`Yakin hapus produk ${product.name}? Tindakan ini tidak dapat dibatalkan.`)) return;
    try {
      const res = await api.delete(`/products/${product.id}`);
      if (res.data.success) {
        toast.success('Produk berhasil dihapus');
        fetchProducts();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleView = (product) => {
    setViewing(product);
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setForm({
      sku: product.sku || '',
      barcode: product.barcode || '',
      name: product.name || '',
      category_id: product.category_id || '',
      unit_id: product.unit_id || '',
      purchase_price: product.purchase_price || '',
      selling_price: product.selling_price || '',
      min_stock: product.min_stock || '',
      tax_id: product.tax_id || '',
      valuation_method: product.valuation_method || 'fifo',
      is_active: product.is_active,
      description: product.description || '',
    });
    setImagePreview(product.image_url || '');
    setShowEditModal(true);
  };

  const resetForm = () => {
    setForm({ sku: '', barcode: '', name: '', category_id: '', unit_id: '', purchase_price: '', selling_price: '', min_stock: '', tax_id: '', valuation_method: 'fifo', is_active: true, description: '' });
    setImageFile(null);
    setImagePreview('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Manajemen Produk</h1>
          <p className="page-subtitle">Kelola stok produk, harga, dan informasi kategori</p>
        </div>
        {hasPermission('create_products') && (
          <button onClick={openCreateModal} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" /> Tambah Produk
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari SKU, barcode, nama..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchProducts()}
              />
            </div>
            <div>
              <select
                className="input"
                value={filters.category_id}
                onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}
              >
                <option value="">Semua Kategori</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <select
                className="input"
                value={filters.unit_id}
                onChange={(e) => setFilters({ ...filters, unit_id: e.target.value })}
              >
                <option value="">Semua Satuan</option>
                {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <select
                className="input"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Semua Status</option>
                <option value="active">Aktif</option>
                <option value="inactive">Nonaktif</option>
              </select>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <div className="flex gap-2">
              <button onClick={fetchProducts} className="btn-primary btn-sm">Cari</button>
              <button onClick={() => setFilters({ search: '', category_id: '', status: '', unit_id: '' })} className="btn-secondary btn-sm">Reset</button>
            </div>
            <div className="inline-flex items-center gap-1 rounded-full border border-surface-200/80 bg-white/70 p-1 dark:border-white/10 dark:bg-surface-900/50" role="group" aria-label="Mode tampilan produk">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                aria-pressed={viewMode === 'grid'}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'grid' ? 'bg-primary-600 text-on-primary' : 'text-surface-500 hover:text-surface-900 dark:hover:text-white'}`}
              >
                <LayoutGrid size={14} aria-hidden="true" /> Grid
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                aria-pressed={viewMode === 'table'}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'table' ? 'bg-primary-600 text-on-primary' : 'text-surface-500 hover:text-surface-900 dark:hover:text-white'}`}
              >
                <List size={14} aria-hidden="true" /> Tabel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-5 lg:p-5">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="rounded-2xl border border-surface-100 dark:border-surface-800 overflow-hidden">
                  <div className="skeleton aspect-square w-full rounded-none" />
                  <div className="space-y-2 p-3.5">
                    <div className="skeleton h-3 w-16" />
                    <div className="skeleton h-4 w-full" />
                    <div className="skeleton h-4 w-2/3" />
                    <div className="skeleton h-5 w-24" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-body">
              <div className="space-y-3">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="flex gap-4 items-center">
                    <div className="skeleton h-10 w-10 rounded-lg" />
                    <div className="skeleton h-4 w-48" />
                    <div className="skeleton h-4 w-36" />
                    <div className="skeleton h-4 w-20" />
                    <div className="skeleton h-4 w-20" />
                    <div className="skeleton h-4 w-24" />
                    <div className="skeleton h-4 w-20" />
                    <div className="skeleton h-4 w-20" />
                  </div>
                ))}
              </div>
            </div>
          )
        ) : products.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada produk yang terdaftar.</p>
          </div>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-5 lg:p-5">
                {products.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDeactivate={handleDeactivate}
                    onDelete={handleDelete}
                    canEdit={hasPermission('edit_products')}
                  />
                ))}
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Produk</th>
                      <th>Kategori</th>
                      <th>Satuan</th>
                      <th>Harga Jual</th>
                      <th>Harga Beli</th>
                      <th>Stok</th>
                      <th>Status</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p) => (
                      <ProductRow
                        key={p.id}
                        product={p}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDeactivate={handleDeactivate}
                        onDelete={handleDelete}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Tambah Produk Baru</h3>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">SKU <span className="text-danger-600">*</span></label>
                  <input type="text" className="input" value={form.sku} onChange={(e) => setForm({...form, sku: e.target.value})} required placeholder="Contoh: PROD-001" />
                </div>
                <div>
                  <label className="label">Barcode</label>
                  <input type="text" className="input" value={form.barcode} onChange={(e) => setForm({...form, barcode: e.target.value})} placeholder="Barcode produk" />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Nama Produk <span className="text-danger-600">*</span></label>
                  <input type="text" className="input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="Nama produk" />
                </div>
                <div>
                  <label className="label">Kategori <span className="text-danger-600">*</span></label>
                  <select className="input" value={form.category_id} onChange={(e) => setForm({...form, category_id: e.target.value})} required>
                    <option value="">Pilih Kategori</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Satuan <span className="text-danger-600">*</span></label>
                  <select className="input" value={form.unit_id} onChange={(e) => setForm({...form, unit_id: e.target.value})} required>
                    <option value="">Pilih Satuan</option>
                    {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Harga Beli</label>
                  <input type="number" className="input" value={form.purchase_price} onChange={(e) => setForm({...form, purchase_price: e.target.value})} placeholder="Harga beli per unit" />
                </div>
                <div>
                  <label className="label">Harga Jual <span className="text-danger-600">*</span></label>
                  <input type="number" className="input" value={form.selling_price} onChange={(e) => setForm({...form, selling_price: e.target.value})} required placeholder="Harga jual per unit" />
                </div>
                <div>
                  <label className="label">Stok Minimum</label>
                  <input type="number" className="input" value={form.min_stock} onChange={(e) => setForm({...form, min_stock: e.target.value})} placeholder="Stok minimum" />
                </div>
                <div>
                  <label className="label">Metode Penilaian</label>
                  <select className="input" value={form.valuation_method} onChange={(e) => setForm({...form, valuation_method: e.target.value})}>
                    <option value="fifo">FIFO</option>
                    <option value="lifo">LIFO</option>
                    <option value="weighted">Weighted Average</option>
                  </select>
                </div>
                <div>
                  <label className="label">Tax ID</label>
                  <select className="input" value={form.tax_id} onChange={(e) => setForm({...form, tax_id: e.target.value})}>
                    <option value="">Tanpa Tax</option>
                    {taxes.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.rate}%)</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Status</label>
                  <select className="input" value={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.value === 'true'})}>
                    <option value="true">Aktif</option>
                    <option value="false">Nonaktif</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="label">Deskripsi</label>
                  <textarea className="input min-h-[80px]" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Deskripsi produk" rows={3} />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Gambar Produk</label>
                  <div className="rounded-2xl border border-dashed border-surface-300 bg-surface-50/60 p-6 text-center dark:border-surface-700 dark:bg-surface-950/40">
                    {imagePreview ? (
                      <div className="space-y-2">
                        <img src={imagePreview} alt="Preview" className="max-h-40 mx-auto rounded" />
                        <button type="button" onClick={() => { setImageFile(null); setImagePreview(''); }} className="text-danger-600 hover:text-danger-700 text-sm">Hapus Gambar</button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="h-12 w-12 text-gray-400 mx-auto" />
                        <p className="text-sm text-gray-500">Klik untuk upload gambar atau drag & drop</p>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" id="image-upload" />
                        <label htmlFor="image-upload" className="btn-outline cursor-pointer">Pilih Gambar</label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Menyimpan...' : 'Simpan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedProduct && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Edit Produk</h3>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">SKU <span className="text-danger-600">*</span></label>
                  <input type="text" className="input" value={form.sku} onChange={(e) => setForm({...form, sku: e.target.value})} required placeholder="Contoh: PROD-001" />
                </div>
                <div>
                  <label className="label">Barcode</label>
                  <input type="text" className="input" value={form.barcode} onChange={(e) => setForm({...form, barcode: e.target.value})} placeholder="Barcode produk" />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Nama Produk <span className="text-danger-600">*</span></label>
                  <input type="text" className="input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="Nama produk" />
                </div>
                <div>
                  <label className="label">Kategori <span className="text-danger-600">*</span></label>
                  <select className="input" value={form.category_id} onChange={(e) => setForm({...form, category_id: e.target.value})} required>
                    <option value="">Pilih Kategori</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Satuan <span className="text-danger-600">*</span></label>
                  <select className="input" value={form.unit_id} onChange={(e) => setForm({...form, unit_id: e.target.value})} required>
                    <option value="">Pilih Satuan</option>
                    {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Harga Beli</label>
                  <input type="number" className="input" value={form.purchase_price} onChange={(e) => setForm({...form, purchase_price: e.target.value})} placeholder="Harga beli per unit" />
                </div>
                <div>
                  <label className="label">Harga Jual <span className="text-danger-600">*</span></label>
                  <input type="number" className="input" value={form.selling_price} onChange={(e) => setForm({...form, selling_price: e.target.value})} required placeholder="Harga jual per unit" />
                </div>
                <div>
                  <label className="label">Stok Minimum</label>
                  <input type="number" className="input" value={form.min_stock} onChange={(e) => setForm({...form, min_stock: e.target.value})} placeholder="Stok minimum" />
                </div>
                <div>
                  <label className="label">Metode Penilaian</label>
                  <select className="input" value={form.valuation_method} onChange={(e) => setForm({...form, valuation_method: e.target.value})}>
                    <option value="fifo">FIFO</option>
                    <option value="lifo">LIFO</option>
                    <option value="weighted">Weighted Average</option>
                  </select>
                </div>
                <div>
                  <label className="label">Tax ID</label>
                  <select className="input" value={form.tax_id} onChange={(e) => setForm({...form, tax_id: e.target.value})}>
                    <option value="">Tanpa Tax</option>
                    {taxes.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.rate}%)</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Status</label>
                  <select className="input" value={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.value === 'true'})}>
                    <option value="true">Aktif</option>
                    <option value="false">Nonaktif</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="label">Deskripsi</label>
                  <textarea className="input min-h-[80px]" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Deskripsi produk" rows={3} />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Gambar Produk</label>
                  <div className="rounded-2xl border border-dashed border-surface-300 bg-surface-50/60 p-6 text-center dark:border-surface-700 dark:bg-surface-950/40">
                    {imagePreview ? (
                      <div className="space-y-2">
                        <img src={imagePreview} alt="Preview" className="max-h-40 mx-auto rounded" />
                        <button type="button" onClick={() => { setImageFile(null); setImagePreview(''); }} className="text-danger-600 hover:text-danger-700 text-sm">Hapus Gambar</button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="h-12 w-12 text-gray-400 mx-auto" />
                        <p className="text-sm text-gray-500">Klik untuk upload gambar atau drag & drop</p>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" id="image-upload-edit" />
                        <label htmlFor="image-upload-edit" className="btn-outline cursor-pointer">Pilih Gambar</label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => { setShowEditModal(false); setSelectedProduct(null); setImageFile(null); setImagePreview(''); }} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Menyimpan...' : 'Perbarui'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewing && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Detail Produk</h3>
              <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">SKU</label>
                  <p className="text-sm text-gray-900 dark:text-white">{viewing.sku}</p>
                </div>
                <div>
                  <label className="label">Barcode</label>
                  <p className="text-sm text-gray-900 dark:text-white">{viewing.barcode || '-'}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="label">Nama</label>
                  <p className="text-sm text-gray-900 dark:text-white">{viewing.name}</p>
                </div>
                <div>
                  <label className="label">Kategori</label>
                  <p className="text-sm text-gray-900 dark:text-white">{viewing.category?.name || '-'}</p>
                </div>
                <div>
                  <label className="label">Satuan</label>
                  <p className="text-sm text-gray-900 dark:text-white">{viewing.unit?.name || '-'}</p>
                </div>
                <div>
                  <label className="label">Harga Beli</label>
                  <p className="text-sm text-gray-900 dark:text-white">{viewing.purchase_price ? formatCurrency(viewing.purchase_price) : '-'}</p>
                </div>
                <div>
                  <label className="label">Harga Jual</label>
                  <p className="text-sm text-gray-900 dark:text-white">{formatCurrency(viewing.selling_price)}</p>
                </div>
                <div>
                  <label className="label">Stok</label>
                  <p className="text-sm text-gray-900 dark:text-white">{viewing.stock}</p>
                </div>
                <div>
                  <label className="label">Stok Minimum</label>
                  <p className="text-sm text-gray-900 dark:text-white">{viewing.min_stock || '-'}</p>
                </div>
                <div>
                  <label className="label">Tax</label>
                  <p className="text-sm text-gray-900 dark:text-white">{viewing.tax?.name || '-'}</p>
                </div>
                <div>
                  <label className="label">Metode Penilaian</label>
                  <p className="text-sm text-gray-900 dark:text-white">{viewing.valuation_method}</p>
                </div>
                <div>
                  <label className="label">Status</label>
                  <span className={`badge ${viewing.is_active ? 'badge-success' : 'badge-danger'}`}>{viewing.is_active ? 'Aktif' : 'Nonaktif'}</span>
                </div>
              </div>
              {viewing.description && (
                <div>
                  <label className="label">Deskripsi</label>
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-line">{viewing.description}</p>
                </div>
              )}
              {viewing.image_url && (
                <div>
                  <label className="label">Gambar</label>
                  <img src={viewing.image_url} alt={viewing.name} className="max-h-40 rounded" />
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setViewing(null)} className="btn-secondary w-full">Tutup</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}